import { Injectable } from '@angular/core';
import { Observable, of, timer } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { map, catchError, debounceTime, distinctUntilChanged, takeUntil, tap } from 'rxjs/operators';
import { throwError } from 'rxjs';
import { TicketFilter } from 'src/app/shared/models/ticket-filter/TicketFilter';
import { TicketFilterSections } from 'src/app/shared/models/ticket-filter-sections/TicketFilterSections';
import { ApiPagingConfig } from 'src/app/shared/models/paging/ApiPagingConfig';
import { DEFAULT_PAGING_CONFIG } from 'src/constants/paging-config';

import { timePeriods } from 'src/app/data/time-periods/time-periods';
import { adaptTimePeriodsToTicketFilters } from '../../utils/ticket-filter/time-period-adapters.util';
import { ApiResponseData } from 'src/app/shared/models/api-response-data/ApiResponseData';
import {
  COMPANY_ADDRESSBOOK_ID,
  CACHED_TICKET_FILTERS,
  TICKET_PRIORITYS,
  TOPIC_TYPES,
  TOPIC_TYPES_MAPPER,
  DEFAULT_TEMPLATES,
  USER_COMPANIES,
  MAX_SATISFACTION
} from 'src/constants/local-storage-variables';
import { TicketStatus } from 'src/app/shared/models/ticket-status/TicketStatus';
import { DEFAULT_TAKE_UNTIL_TIME } from 'src/constants/times';
import { TicketPriority } from 'src/app/shared/models/ticket-priority/ticket.priority';
import {TopicType, TopicTypeTemplate} from 'src/app/shared/models/ticket/TopicTypes';
import { DefaultTemplates } from 'src/app/shared/models/ticket/DefaultTemplates';
import { TicketFactory } from 'src/app/shared/models/ticket/TicketFactory';
import { EnvironmentResolveService } from '../environment-resolve/environment-resolve.service';
import { AnyNaptrRecord } from 'dns';
import { CUSTOMER_PORTAL_CONFIG } from '../../../../constants/local-storage-variables';
import {
  MAIN_TICKET_FEATURE,
  SHOW_CATEGORY_FROM_BUSINESS_SERVICE,
  CATEGORY_LEVEL
} from '../../../../constants/configuration-variables';
import * as localStorageConstants from '../../../../constants/local-storage-variables';

@Injectable({
  providedIn: 'root'
})
export class TicketService {
  getMessageBody(id: any): Observable<string> {
    const body = {
      application: 'Email',
      method: 'getMessageBody',
      parameters: {
        id
      }
    };
    return this.post(body);
  }
  constructor(
    private http: HttpClient,
    private erService: EnvironmentResolveService) {}

  /**
   * @info ticketType - see constants/ticket-types.ts
   */
  public searchTicketFilters(
    ticketFilterSection: TicketFilterSections,
    ticketType: string[],
    paging: ApiPagingConfig = DEFAULT_PAGING_CONFIG
  ): Observable<ApiResponseData<TicketFilter[]>> {
    let method: string;
    let model: string;
    let body: object;
    let parameters: object;
    let application = 'CPM';
    let companies = {};

    const filters: TicketFilter[] = [
      { field: 'topic_type', operator: 'in', value: ticketType }
    ];

    const cpConfig = JSON.parse(localStorage.getItem(CUSTOMER_PORTAL_CONFIG));
    if (cpConfig && cpConfig.hasOwnProperty(MAIN_TICKET_FEATURE) && cpConfig[MAIN_TICKET_FEATURE]) {
      filters.push({
        field: 'is_main',
        operator: 'equals',
        value: 1
      });
    }
    console.log('ticketFilterSection', ticketFilterSection);
    switch (ticketFilterSection) {
      case 'category':
        application = 'CPM';
        method = 'searchCategorys';

        const level = (cpConfig && cpConfig.hasOwnProperty(CATEGORY_LEVEL) && cpConfig[CATEGORY_LEVEL]) ? cpConfig[CATEGORY_LEVEL] : '';
        const showCategoryFromBusinessService = cpConfig?.[SHOW_CATEGORY_FROM_BUSINESS_SERVICE];
        const filter = [
          { field: 'query', operator: 'contains', value: '' },
          { field: 'status', operator: 'equals', value: true },
          { field: 'topic_type', operator: 'in', value: [-1, ticketType[0]] }
        ];

        if (level !== '') {
          filter.push({ field: 'level', operator: 'equals', value: level });
        }
        if (showCategoryFromBusinessService) {
          filter.push({ field: 'reporter_categories', operator: 'equals', value: true });
        }
        parameters = {
          filter,
          paging: {
            dir: ['DESC', 'ASC', 'ASC'],
            sort: ['topic_type', 'level', 'name']
          }
        };

        break;
      case 'reporter':
        application = 'Tinebase';
        method = 'getRelations';
        companies = JSON.parse(localStorage.getItem(localStorageConstants.USER_COMPANIES));
        const companiesIds = Object.values(companies).map(com => com['id']);
        parameters = {
          model: 'Addressbook_Model_Contact',
          id: companiesIds,
          degree: 'sibling',
          type: ['REPORTER'],
          relatedModel: 'Addressbook_Model_Contact'
        };
        break;
      case 'last modified':
        application = 'SLM';
        method = 'getCompanyData';
        model = 'Service';
        parameters = {companyId: null, model};
        break;

      case 'services':
        application = 'SLM';
        method = 'getCompanyData';
        model = 'Service';
        parameters = {companyId: null, model};
        break;
      case 'priorities':
        application = 'SLM';
        method = 'geUserPriority';
        parameters = {};
        break;
      case 'urgencies':
        application = 'SLM';
        method = 'getCompanyData';
        model = 'Urgency';
        parameters = {companyId: null, model};
        break;
      case 'impacts':
        application = 'SLM';
        method = 'getCompanyData';
        model = 'Impact';
        parameters = {companyId: null, model};
        break;
      case 'companies':
        application = 'SLM';
        method = 'getUserCompanies';
        parameters = {};
        break;

      case 'statuses':
        method = 'searchStates';
        parameters = { filter: filters,  paging};
        break;

      case 'customer_state':
        method = 'searchCustomerStates';
        parameters = { filter: filters,  paging};
        break;

      default:
        method = '';
        parameters = { filter: filters,  paging};
        break;

    }

    body = {
      application,
      method,
      parameters
    };

    switch (ticketFilterSection) {
      case 'last modified':
      case 'services':
      case 'urgencies':
      case 'impacts':
      case 'priorities':
        return this.http.post(this.erService.environmentERS.apiUrl, body).pipe(
          map((data: any[]) => {
            return <ApiResponseData<TicketFilter[]>>{
              results: data,
              totalcount: data.length
            };
          }),
          debounceTime(400),
          distinctUntilChanged()
        );

      default:
        return this.http.post(this.erService.environmentERS.apiUrl, body).pipe(
          map((data: ApiResponseData<any[]>) => {
            // extract company names for company filter section
            if (ticketFilterSection === 'companies') {
              data.results = data.results.map((ticketFilter: any) => {
                ticketFilter.name = ticketFilter.org_name;
                return ticketFilter;
              });
            }
            if (ticketFilterSection === 'reporter') {
              data.results = data.results.map((ticketFilter: any) => {
                ticketFilter.name = ticketFilter.related_record.n_fn;
                ticketFilter.id = ticketFilter.related_record.id;
                return ticketFilter;
              });
            }
            if (ticketFilterSection === 'category') {
              data.results = data.results.map((ticketFilter: any) => {
                if (ticketFilter.parent) {
                  ticketFilter.name = '(' + ticketFilter.parent.name + ') ' + ticketFilter.name;
                } else {
                  ticketFilter.name = ticketFilter.name;
                }
                return ticketFilter;
              });
            }
            return <ApiResponseData<TicketFilter[]>>data;
          }),
          debounceTime(400),
          distinctUntilChanged()
        );
    }
  }

  public post(body: any) {
    return this.http.post(this.erService.environmentERS.apiUrl, body).pipe(
      map((data: any) => {
        return data;
      }),
      catchError((error: any) => {
        return throwError(error);
      })
    );
  }

  public cacheTicketStatuses(): void {
    const body = {
      application: 'CPM',
      method: 'searchStates',
      parameters: {
        filter: [],
        paging: {}
      }
    };

    this.http
      .post(this.erService.environmentERS.apiUrl, body)
      .pipe(
        map((response: ApiResponseData<TicketStatus[]>) => {
          const ticketStatusList: TicketStatus[] = response.results;
          localStorage.setItem(CACHED_TICKET_FILTERS, JSON.stringify(ticketStatusList));
        }),
        takeUntil(timer(DEFAULT_TAKE_UNTIL_TIME))
      )
      .subscribe();
  }

  public getTicketStatuses(topicType: string): Observable<{ [key: string]: TicketStatus }> {
    const body = {
      application: 'CPM',
      method: 'searchStates',
      parameters: {
        filter: [{ field: 'topic_type', operator: 'equals', value: topicType }],
        paging: {}
      }
    };

    const cached = localStorage.getItem(TicketFactory.ticketStatuses[topicType]);
    if (cached) {
      try {
        const ticketStatusList = JSON.parse(cached);
        return of(ticketStatusList);
      } catch {}
    }
    return this.http.post(this.erService.environmentERS.apiUrl, body).pipe(
      map((response: ApiResponseData<any>) =>
        response.results.reduce((resultMap, status) => {
          const customfields = {};
          status.topic_type.customfield?.results?.forEach(field => {
            if (field.definition.keyFieldConfig) {
              customfields[field.name] = field.definition.keyFieldConfig.value.records.reduce(
                (variantsMap, variant) => {
                  variantsMap[variant.id] = variant.value;
                  return variantsMap;
                },
                {}
              );
            }
          });

          resultMap[status.id] = {
            name: status.name,
            customfields
          };
          return resultMap;
        }, {})
      ),
      tap((ticketStatusList: { [key: string]: TicketStatus }) => {
        localStorage.setItem(TicketFactory.ticketStatuses[topicType], JSON.stringify(ticketStatusList));
      })
    );
  }

  public getTicketPrioritys(): Observable<{ [key: string]: string }> {
    const body = {
      application: 'CPM',
      method: 'searchPrioritys',
      parameters: {
        filter: [],
        paging: {}
      }
    };
    const cached = localStorage.getItem(TICKET_PRIORITYS);

    if (cached) {
      try {
        const ticketPriorityList = JSON.parse(cached);
        return of(ticketPriorityList);
      } catch {}
    }

    return this.http.post(this.erService.environmentERS.apiUrl, body).pipe(
      map((response: ApiResponseData<TicketPriority[]>) =>
        response.results.reduce((resultMap, obj) => {
          resultMap[obj.id] = obj.name;
          return resultMap;
        }, {})
      ),
      tap((ticketPriorityList: { [key: string]: string }) => {
        localStorage.setItem(TICKET_PRIORITYS, JSON.stringify(ticketPriorityList));
      })
    );
  }

  public getRegistryData(): Observable<{ [key: string]: string }> {
    try {
      const topicTypes = JSON.parse(localStorage.getItem(TOPIC_TYPES));
      if (topicTypes) {
        TicketFactory.setTicketTypes(topicTypes);
        return of(topicTypes);
      }
    } catch {}
    return this.http
      .post<{ topicTypeOperations: TopicType[], topicTypeMapper: [], templates: any, maxSatisfaction: string }>(this.erService.environmentERS.apiUrl, {
        application: 'CPM',
        method: 'getRegistryData',
        parameters: {}
      })
      .pipe(
        map(({ topicTypeOperations, topicTypeMapper, templates, maxSatisfaction }) => {
          const topicTypes = topicTypeOperations.reduce((result, item: TopicType) => {
            result[item.operation.toUpperCase().replace(new RegExp(' ', 'g'), '_')] = item.topic_type;
            return result;
          }, {});
          const topicTypeMappers = topicTypeMapper || [];
          localStorage.setItem(TOPIC_TYPES_MAPPER, JSON.stringify(topicTypeMappers));
          localStorage.setItem(TOPIC_TYPES, JSON.stringify(topicTypes));
          localStorage.setItem(DEFAULT_TEMPLATES, JSON.stringify(templates));
          localStorage.setItem(MAX_SATISFACTION, JSON.stringify(maxSatisfaction));
          TicketFactory.setTicketTypes(topicTypes);
          return topicTypes;
        })
      );
  }


  public getTemplates(): Observable<{ [key: string]: string }> {
    return this.http
      .post<{ topicTypeTemplateOperations: TopicTypeTemplate[] }>(this.erService.environmentERS.apiUrl, {
        application: 'CPM',
        method: 'searchTemplates',
        parameters: {}
      })
      .pipe(
        map(({ topicTypeTemplateOperations }) => {
          const topicTypeTemplates = topicTypeTemplateOperations.reduce((result, item: TopicTypeTemplate) => {
            result[item.operation.toUpperCase().replace(new RegExp(' ', 'g'), '_')] = item.topic_type;
            return result;
          }, {});
          localStorage.setItem(DEFAULT_TEMPLATES, JSON.stringify(topicTypeTemplates));
          TicketFactory.setTicketTypes(topicTypeTemplates);
          return topicTypeTemplates;
        }),
      );
  }

  public getServiceCatalogRegistryData(): Observable<{ [key: string]: string }> {
    console.log('getServiceCatalogRegistryData call');
    /*
    try {
      const defaultTemplates = JSON.parse(localStorage.getItem(DEFAULT_TEMPLATES));
      console.log('getServiceCatalogRegistryData', defaultTemplates);
      if (defaultTemplates) {
        TicketFactory.setDefaultTemplates(defaultTemplates);
        return of(defaultTemplates);
      }
    } catch {}
    */

    return this.http
      .post(this.erService.environmentERS.apiUrl, {
        application: 'ServiceCatalog',
        method: 'getRegistryData',
        parameters: {}
      }).pipe(
        map((response: ApiResponseData<any>) =>
          response.results.reduce((resultMap, template) => {
            const customfields = {};
            /* template.topic_type.customfield.results.forEach(field => {
               if (field.definition.keyFieldConfig) {
                 customfields[field.name] = field.definition.keyFieldConfig.value.records.reduce(
                   (variantsMap, variant) => {
                     variantsMap[variant.id] = variant.value;
                     return variantsMap;
                   },
                   {}
                 );
               }
             });*/
            console.log('getServiceCatalogRegistryData resultMap', response);
            resultMap[template.id] = {
              name: template.name,
              customfields
            };
            return resultMap;
          }, {})
        ),
        // //   tap((ticketStatusList: { [key: string]: TicketStatus }) => {
        //      localStorage.setItem(DEFAULT_TEMPLATES, JSON.stringify(ticketStatusList));
        //    })
      );
  }

  public getTicketStatusesFromCache(): TicketStatus[] {
    const ticketStatusList: TicketStatus[] = JSON.parse(localStorage.getItem(CACHED_TICKET_FILTERS));

    return ticketStatusList;
  }

  public getTicketInputLayout(topicType: number = 0, templateId: number = 0, to: string = '', contact: string = '', itemId: string = '') {
    console.log('itemId', itemId);
    const body = {
      application: 'CPM',
      method: 'getInputPropertiesOfTopicType',
      parameters: {
        topic_type: topicType,
        template_id: templateId,
        to_id: to,
        contact_id: contact,
        item_id: itemId
      }
    };
    return this.http.post(this.erService.environmentERS.apiUrl, body);
  }

  public getTicketOutputLayout(topicType, topicId = '') {
    const body = {
      application: 'CPM',
      method: 'getOutputPropertiesOfTopicType',
      parameters: {
        topicType,
        topicId
      }
    };
    return this.http.post(this.erService.environmentERS.apiUrl, body);
  }

  public getTicketRelations(topicId = '', type = []) {
    const body = {
      application: 'Tinebase',
      method: 'getRelations',
      parameters: {
        model: 'CPM_Model_Topic',
        id: topicId,
        degree: null,
        type,
        relatedModel: null,
        getBody: true
      }
    };
    return this.http.post(this.erService.environmentERS.apiUrl, body);
  }


  public action(payload: string) {
    return this.http.post(this.erService.environmentERS.apiUrl, payload);
  }

  public getTemplateTopicTypeById(templateId: number) {
    const templates = JSON.parse(localStorage.getItem(DEFAULT_TEMPLATES));
    for (const x in templates) {
      if (templates.hasOwnProperty(x)) {
        if (templates[x].id === templateId.toString()) {
          console.log('templateId', templateId);
          console.log('templateId: ' + templates[x].id, 'topicType: ' + templates[x].topic_type);
          return templates[x].topic_type;
        }
      }
    }
  }

  public getTopicWatchers(topicId: string, notificationType: string) {
    const body = {
      application: 'CPM',
      method: 'getTopicWatchers',
      parameters: {
        topicId,
        notificationType
      }
    };
    return this.http.post(this.erService.environmentERS.apiUrl, body);
  }
}
